/**
 * Meta (Facebook) Pixel helper for the app domain (app.jyotishpuraan.com).
 *
 * WHY THIS EXISTS
 * The landing site (jyotishpuraan.com) already has the Pixel and fires
 * PageView / Lead / CompleteRegistration. But the *actual* conversions —
 * real signup, trial activation, ChitraGupt session, payment — happen here
 * on the app domain, which had NO pixel. Meta therefore never saw who
 * actually converted and could not optimise ad delivery past the click.
 *
 * This module loads the SAME pixel ID used on the landing site so Meta sees
 * one continuous journey (ad -> landing -> app -> register -> trial -> pay).
 *
 * DESIGN
 * - Env-gated on REACT_APP_META_PIXEL_ID (CRA inlines this at build time).
 *   If the var is empty, every export is a safe no-op. => merge-safe before
 *   the ID is configured in Emergent.
 * - Advanced matching: setMetaUserData() passes raw email/phone to the pixel,
 *   which hashes them in-browser (SHA-256) before sending. Improves match
 *   quality so Meta can attribute more conversions.
 * - Deduplication: every conversion gets an eventId. Pass the SAME eventId to
 *   the server-side Conversions API (backend/utils/meta_capi.py) so Meta
 *   counts the browser event and the server event as one.
 */

const PIXEL_ID = process.env.REACT_APP_META_PIXEL_ID || '';
const ENABLED = Boolean(PIXEL_ID);

let initialised = false;

/** Generate a unique event id for browser<->server deduplication. */
export function newEventId() {
  try {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  } catch (_) { /* ignore */ }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

/** Load fbevents.js, init the pixel and fire the initial PageView. Idempotent. */
export function initMetaPixel() {
  if (!ENABLED || initialised || typeof window === 'undefined') return;
  initialised = true;

  /* eslint-disable */
  !(function (f, b, e, v, n, t, s) {
    if (f.fbq) return;
    n = f.fbq = function () {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
    };
    if (!f._fbq) f._fbq = n;
    n.push = n; n.loaded = !0; n.version = '2.0'; n.queue = [];
    t = b.createElement(e); t.async = !0;
    t.src = v; s = b.getElementsByTagName(e)[0]; s.parentNode.insertBefore(t, s);
  })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
  /* eslint-enable */

  window.fbq('init', PIXEL_ID);
  window.fbq('track', 'PageView');
}

/**
 * Attach known identifiers for advanced matching. Call as soon as you know the
 * user's phone/email (e.g. right after signup). The pixel hashes these in the
 * browser — raw values are never sent to Meta.
 * @param {{ email?: string, phone?: string, externalId?: string }} user
 */
export function setMetaUserData(user = {}) {
  if (!ENABLED || typeof window === 'undefined' || !window.fbq) return;
  const ud = {};
  if (user.email) ud.em = String(user.email).trim().toLowerCase();
  if (user.phone) ud.ph = String(user.phone).replace(/\D/g, '');
  if (user.externalId) ud.external_id = String(user.externalId);
  if (Object.keys(ud).length) window.fbq('set', 'userData', ud);
}

/**
 * Fire a Meta standard (or custom) event with dedup support.
 * @param {string} eventName e.g. 'CompleteRegistration', 'StartTrial', 'Subscribe', 'Purchase'
 * @param {object} customData e.g. { currency: 'INR', value: 499 }
 * @param {{ eventId?: string }} opts pass an eventId to dedup against the server CAPI event
 * @returns {string} the eventId used (send this to the backend if mirroring server-side)
 */
export function trackMeta(eventName, customData = {}, opts = {}) {
  const eventId = opts.eventId || newEventId();
  if (!ENABLED || typeof window === 'undefined' || !window.fbq) return eventId;
  try {
    window.fbq('track', eventName, customData, { eventID: eventId });
  } catch (_) { /* never let analytics break the app */ }
  return eventId;
}

/** True when a pixel ID is configured. Useful for guarding callers. */
export const metaPixelEnabled = ENABLED;

function _readCookie(name) {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
}

/**
 * Read the `_fbp` browser cookie the Pixel script sets itself. Send this to
 * the backend alongside any CAPI-firing request (registration, lead, order
 * creation) — it's one of the two identifiers Meta's match-quality banner
 * flags as low-coverage on this dataset.
 */
export function getFbp() {
  return _readCookie('_fbp');
}

/**
 * Read (or synthesize) the `_fbc` identifier. Prefers the `_fbc` cookie Meta
 * sets itself; falls back to building it from a `fbclid` URL param the same
 * way the backend's `_fbc_from_fbclid` does, for the case where this is the
 * very first page view and the cookie hasn't been set yet.
 */
export function getFbc() {
  const existing = _readCookie('_fbc');
  if (existing) return existing;
  if (typeof window === 'undefined') return null;
  try {
    const fbclid = new URLSearchParams(window.location.search).get('fbclid');
    if (fbclid) return `fb.1.${Date.now()}.${fbclid}`;
  } catch (_) { /* ignore */ }
  return null;
}
